import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { CheckCircle2, Circle, ClipboardList, Send } from 'lucide-react'
import { type ChangeEvent, type MouseEvent, useState } from 'react'

import { useApi } from '@/hooks/use-api'
import { type ApiClient, ApiError, type PaginatedResponse } from '@/lib/api'
import {
  formatBonusMinutes,
  formatDateLong,
  formatDateShort,
  formatHours,
} from '@/lib/format'
import { cn } from '@/lib/utils'

import type { ClientLite } from '@/components/planning/planning-types'
import type { MissionListRow } from './mission-types'

/**
 * Missions table — the workhorse of the admin evening review.
 *
 * Each row carries:
 *   - Date (compact, with full-form tooltip)
 *   - Site
 *   - Driver name + vehicle plate
 *   - Client (name + colour pill)
 *   - Total / day / night / bonus hours
 *   - Two inline toggles: `verified` and `readyToSend`
 *
 * Clicking anywhere on the row (except the toggles) navigates to
 * the detail page. Toggles use TanStack Query's
 * `onMutate`/`onError`/`onSettled` for true optimistic updates —
 * the cell flips instantly, the network call goes out in the
 * background, and a failure rolls back without blanking the cell
 * to "loading".
 *
 * Why not Radix Checkbox
 * ----------------------
 * The native `<input type="checkbox">` is enough here: we don't
 * need an indeterminate state and the visual is just a brand-
 * coloured tick. Avoids one more Radix dep without losing
 * accessibility (still keyboard-toggleable, focusable, labelled).
 */

interface MissionsTableProps {
  rows: readonly MissionListRow[]
  clients: readonly ClientLite[]
  /** Query key whose cached response we mutate optimistically. */
  queryKey: readonly unknown[]
  /** Optional empty-state slot. */
  emptyMessage?: string
}

export function MissionsTable({
  rows,
  clients,
  queryKey,
  emptyMessage = 'Aucune mission ne correspond aux filtres.',
}: MissionsTableProps) {
  const api = useApi()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [toggleError, setToggleError] = useState<string | null>(null)

  const verifyMutation = useToggleMutation(api, queryClient, queryKey, 'verify', setToggleError)
  const readyMutation = useToggleMutation(api, queryClient, queryKey, 'ready', setToggleError)

  if (rows.length === 0) {
    return (
      <div className="px-6 py-12 text-center text-text-muted">
        <ClipboardList size={28} className="mx-auto mb-2 opacity-60" aria-hidden="true" />
        <p>{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      {toggleError !== null ? (
        <p
          role="alert"
          className="px-4 py-2 text-sm text-danger bg-danger-soft border-b border-danger/30"
        >
          {toggleError}
        </p>
      ) : null}
      <table className="w-full text-sm">
        <thead className="bg-surface-muted text-text-muted">
          <tr className="text-left">
            <Th className="w-20">Date</Th>
            <Th>Chantier</Th>
            <Th>Chauffeur</Th>
            <Th>Véhicule</Th>
            <Th>Client</Th>
            <Th className="text-right w-20">Total</Th>
            <Th className="text-right w-20">Jour</Th>
            <Th className="text-right w-20">Nuit</Th>
            <Th className="text-right w-20">Bonus</Th>
            <Th className="text-center w-24">Vérifié</Th>
            <Th className="text-center w-24">Prêt</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const clientColor =
              clients.find((c) => c.id === row.clientId)?.color ?? null
            return (
              <Row
                key={row.id}
                row={row}
                clientColor={clientColor}
                onOpen={() =>
                  void navigate({
                    to: '/missions/$id',
                    params: { id: row.id },
                  })
                }
                onToggleVerify={(value) =>
                  verifyMutation.mutate({ id: row.id, value })
                }
                onToggleReady={(value) =>
                  readyMutation.mutate({ id: row.id, value })
                }
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  One row                                                                    */
/* -------------------------------------------------------------------------- */

interface RowProps {
  row: MissionListRow
  clientColor: string | null
  onOpen: () => void
  onToggleVerify: (value: boolean) => void
  onToggleReady: (value: boolean) => void
}

function Row({ row, clientColor, onOpen, onToggleVerify, onToggleReady }: RowProps) {
  // The row is clickable, but the two toggle cells need to NOT
  // navigate when clicked. We do that by stopping propagation on
  // the toggle's wrapping cell, not on the input itself, so even
  // a click on the surrounding label area stays put.
  return (
    <tr
      onClick={onOpen}
      className={cn(
        'border-t border-surface-border cursor-pointer',
        'hover:bg-surface-hover transition-colors',
      )}
    >
      <Td className="font-mono tabular-nums text-text" title={formatDateLong(row.date)}>
        {formatDateShort(row.date)}
      </Td>
      <Td className="font-medium text-text">{row.site}</Td>
      <Td className="text-text-muted">{row.driverName ?? '—'}</Td>
      <Td className="text-text-muted">{row.vehicleRegistration ?? '—'}</Td>
      <Td>
        {row.clientName !== null ? (
          <span className="inline-flex items-center gap-2">
            <span
              aria-hidden="true"
              className="w-2 h-2 rounded-full shrink-0"
              style={{ background: clientColor ?? '#cbd5e1' }}
            />
            <span className="text-text">{row.clientName}</span>
          </span>
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </Td>
      <Td className="text-right font-mono tabular-nums font-semibold text-text">
        {formatHours(row.totalHours)}
      </Td>
      <Td className="text-right font-mono tabular-nums text-text-muted">
        {formatHours(row.dayHours)}
      </Td>
      <Td className="text-right font-mono tabular-nums text-text-muted">
        {formatHours(row.nightHours)}
      </Td>
      <Td className="text-right font-mono tabular-nums text-text-muted">
        {row.bonusMinutes > 0 ? formatBonusMinutes(row.bonusMinutes) : '—'}
      </Td>
      <ToggleCell
        checked={row.verified}
        onChange={onToggleVerify}
        ariaLabel={`Marquer la mission du ${formatDateShort(row.date)} ${row.verified ? 'comme non vérifiée' : 'comme vérifiée'}`}
        Icon={CheckCircle2}
        tone="success"
      />
      <ToggleCell
        checked={row.readyToSend}
        onChange={onToggleReady}
        ariaLabel={`Marquer la mission du ${formatDateShort(row.date)} ${row.readyToSend ? 'comme non prête' : 'comme prête à envoyer'}`}
        Icon={Send}
        tone="info"
      />
    </tr>
  )
}

interface ToggleCellProps {
  checked: boolean
  onChange: (value: boolean) => void
  ariaLabel: string
  Icon: typeof CheckCircle2
  tone: 'success' | 'info'
}

function ToggleCell({ checked, onChange, ariaLabel, Icon, tone }: ToggleCellProps) {
  return (
    <td
      className="px-3 py-2.5 text-center"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation()
          onChange(!checked)
        }}
        className={cn(
          'inline-flex items-center justify-center',
          'h-8 w-8 rounded-full transition-all',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
          checked
            ? tone === 'success'
              ? 'text-success bg-success-soft hover:bg-success/20'
              : 'text-info bg-info-soft hover:bg-info/20'
            : 'text-text-muted hover:bg-surface-muted',
        )}
      >
        {checked ? (
          <Icon size={18} aria-hidden="true" />
        ) : (
          <Circle size={18} aria-hidden="true" />
        )}
      </button>
    </td>
  )
}

/* -------------------------------------------------------------------------- */
/*  Mutation factory — verify / ready share the exact same shape               */
/* -------------------------------------------------------------------------- */

interface ToggleVars {
  id: string
  value: boolean
}

function useToggleMutation(
  api: ApiClient,
  queryClient: ReturnType<typeof useQueryClient>,
  queryKey: readonly unknown[],
  endpoint: 'verify' | 'ready',
  onErrorMessage: (msg: string) => void,
) {
  const field: 'verified' | 'readyToSend' =
    endpoint === 'verify' ? 'verified' : 'readyToSend'

  return useMutation({
    mutationFn: ({ id, value }: ToggleVars) =>
      api.patch<unknown>(`/api/missions/${id}/${endpoint}`, { value }),
    onMutate: async ({ id, value }) => {
      // Pause refetches so the optimistic write isn't immediately
      // overwritten by an in-flight GET.
      await queryClient.cancelQueries({ queryKey })

      const prev = queryClient.getQueryData<PaginatedResponse<MissionListRow>>(queryKey)
      if (prev !== undefined) {
        queryClient.setQueryData<PaginatedResponse<MissionListRow>>(queryKey, {
          ...prev,
          data: prev.data.map((row) =>
            row.id === id ? { ...row, [field]: value } : row,
          ),
        })
      }
      return { prev }
    },
    onError: (err, _vars, ctx) => {
      // Roll back to the snapshot we took in onMutate.
      if (ctx?.prev !== undefined) queryClient.setQueryData(queryKey, ctx.prev)
      onErrorMessage(
        err instanceof ApiError
          ? err.message
          : "Modification impossible. Veuillez réessayer.",
      )
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey })
    },
  })
}

/* -------------------------------------------------------------------------- */
/*  Plain table cell helpers                                                   */
/* -------------------------------------------------------------------------- */

function Th({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <th
      scope="col"
      className={cn(
        'px-3 py-2 font-medium uppercase tracking-wide text-xs',
        className,
      )}
    >
      {children}
    </th>
  )
}

function Td({
  className,
  children,
  title,
}: {
  className?: string
  children: React.ReactNode
  title?: string
}) {
  return (
    <td className={cn('px-3 py-2.5', className)} title={title}>
      {children}
    </td>
  )
}

/* -------------------------------------------------------------------------- */
/*  Toggle changeEvent helper (legacy, kept exported in case a future          */
/*  caller wants to drive the same mutation outside the row).                  */
/* -------------------------------------------------------------------------- */

export function checkboxValue(event: ChangeEvent<HTMLInputElement>): boolean {
  return event.target.checked
}
